/**
 * Photo Diagnostic — usage tracker (localStorage).
 *
 * Controls whether the "bold" feature-spotlight card appears at the top
 * of the home feed. We want to teach users about the feature aggressively
 * at first (big card + ✨ חדש tag), then fade to a small grid card once
 * they've seen it enough or used it a few times.
 *
 * Two signals determine "bold mode":
 *   - `c2c_photoDiag_firstSeenAt`  : timestamp of first card render
 *   - `c2c_photoDiag_usageCount`   : count of successful photo diagnoses
 *
 * Bold mode ends when EITHER:
 *   - 14 days have passed since the first render, OR
 *   - The user has successfully diagnosed 3+ photos.
 *
 * After that the user only sees the small mini-card in the home grid.
 */

const FIRST_SEEN_KEY = 'c2c_photoDiag_firstSeenAt';
const USAGE_COUNT_KEY = 'c2c_photoDiag_usageCount';
const BOLD_TTL_MS = 14 * 24 * 60 * 60 * 1000;
const BOLD_USAGE_THRESHOLD = 3;

/**
 * Should the bold hero card be shown above the home grid?
 * Idempotent — calling this also sets `firstSeenAt` on first call.
 * @returns {boolean}
 */
export function shouldShowBoldCard() {
  try {
    const usage = Number(localStorage.getItem(USAGE_COUNT_KEY) || 0);
    if (Number.isFinite(usage) && usage >= BOLD_USAGE_THRESHOLD) return false;

    const firstSeen = Number(localStorage.getItem(FIRST_SEEN_KEY) || 0);
    if (!firstSeen || !Number.isFinite(firstSeen) || firstSeen <= 0) {
      try { localStorage.setItem(FIRST_SEEN_KEY, String(Date.now())); } catch (_) { /* quota */ }
      return true;
    }
    return Date.now() - firstSeen < BOLD_TTL_MS;
  } catch (_) { return true; }
}

/**
 * Increment the success counter. Call this AFTER a photo has been
 * successfully sent and acknowledged by the AI.
 */
export function incrementUsage() {
  try {
    const current = Number(localStorage.getItem(USAGE_COUNT_KEY) || 0);
    const next = Number.isFinite(current) ? current + 1 : 1;
    localStorage.setItem(USAGE_COUNT_KEY, String(next));
  } catch (_) { /* ignore */ }
}

/**
 * Read the success counter — used e.g. to show "X אבחונים השבוע" in
 * the bold card body if we want gamification.
 * @returns {number}
 */
export function getUsageCount() {
  try {
    const n = Number(localStorage.getItem(USAGE_COUNT_KEY) || 0);
    return Number.isFinite(n) ? n : 0;
  } catch (_) { return 0; }
}
